import {
  inject,
  Injectable } from '@angular/core';
import { ConfigService } from '@rxap/config';
import { coerceArray } from '@rxap/utilities';
import { Client } from 'typesense';
import type {
  SearchResponse as TypesenseSearchResponse,
  SearchResponseHit as TypesenseSearchResponseHit,
} from 'typesense/lib/Typesense/Documents';
import { confidentialityStore } from '../confidentiality/confidentiality.store';
import { ConfidentialityLevelClassificationIdentifier } from '../consts/classification-identifier';
import { productStore } from '../stores/product.store';
import {
  FilterMatchMode,
  SearchHit,
  SearchParams,
  SearchResponse,
  SearchResultDocument,
} from './search-types';

@Injectable({
  providedIn: 'root',
})
export class TypesenseService {
  readonly products = inject(productStore);
  readonly confidentiality = inject(confidentialityStore);

  get productCollection() {
    return this.config.get('search.typesense.collections.product');
  }

  get informationUnitCollection() {
    return this.config.get('search.typesense.collections.informationUnit');
  }

  readonly disabled: boolean;

  readonly client!: Client;

  constructor(private readonly config: ConfigService) {
    const nodes = coerceArray(
      config.get<string[]>('search.typesense.urls', [])
    ).map((url) => ({ url }));
    const apiKey = config.get<string>('search.typesense.apiKey', '');
    const enabled = config.get<boolean>('search.typesense.enabled', false);
    this.disabled = !enabled || !apiKey || !nodes.length;
    if (!this.disabled) {
      this.client = new Client({
        nodes,
        apiKey,
      });
    }
  }

  async isHealthy(): Promise<boolean> {
    if (this.disabled) {
      return false;
    }
    try {
      const response = await this.client.health.retrieve();
      return response.ok;
    } catch (error: any) {
      console.error(`Typesense server is not healthy: ${error.message}`);
      return false;
    }
  }

  private buildFilterBy({
    informationTypes,
    informationSubjects,
    informationUnits,
    documentTypes,
    productMetadata,
    components,
    cid,
    language,
  }: {
    informationTypes?: string[];
    informationSubjects?: string[];
    informationUnits?: string[];
    documentTypes?: string[];
    productMetadata?: string[];
    components?: string[];
    cid?: string | null;
    language?: string | null;
  } = {}): { scope: string[]; user: string[] } {
    // Scope clauses (cid, language, product, confidentiality) constrain *which*
    // documents are visible and are always AND'ed. User clauses are the
    // category filters the user picks in the Advanced Filters panel; they are
    // combined with each other according to the selected match mode (see
    // `combineFilterBy`).
    const scope: string[] = [];
    const user: string[] = [];
    if (cid) {
      scope.push(`cid:=${cid}`);
    }
    if (language) {
      scope.push(`language:=${language}`);
    }
    const products = this.products.productIds();
    if (products.length) {
      const values = products.map((id) => `\`${id}\``).join(',');
      scope.push(`productId:=[${values}]`);
    }
    if (this.confidentiality.isConfidentialMode()) {
      if (!this.confidentiality.isAuthenticated()) {
        scope.push(
          `confidentialityLevel:=${ConfidentialityLevelClassificationIdentifier.PUBLIC}`
        );
      }
    }
    // Wraps values in backticks ` ` to handle spaces/special chars
    if (informationUnits?.length) {
      const values = informationUnits.map((t) => `\`${t}\``).join(',');
      user.push(`iri:=[${values}]`);
    }
    if (informationTypes?.length) {
      const values = informationTypes.map((t) => `\`${t}\``).join(',');
      user.push(`informationTypes:=[${values}]`);
    }
    if (informationSubjects?.length) {
      const values = informationSubjects.map((t) => `\`${t}\``).join(',');
      user.push(`informationSubjects:=[${values}]`);
    }
    if (documentTypes?.length) {
      const values = documentTypes.map((t) => `\`${t}\``).join(',');
      user.push(`documentTypes:=[${values}]`);
    }
    if (productMetadata?.length) {
      const values = productMetadata.map((t) => `\`${t}\``).join(',');
      user.push(`productMetadata:=[${values}]`);
    }
    if (components?.length) {
      const values = components.map((t) => `\`${t}\``).join(',');
      user.push(`components:=[${values}]`);
    }
    return { scope, user };
  }

  /**
   * Serializes the scope/user clauses into a Typesense `filter_by` string.
   * Scope clauses are always AND'ed. User clauses are AND'ed in `all` mode and
   * OR'ed (wrapped in parentheses) in `any` mode. In `all` mode with no scoped
   * pin this produces the exact same string as before match mode existed.
   *
   * Note: parenthesized `filter_by` grouping requires Typesense >= 0.25.
   */
  private combineFilterBy(
    { scope, user }: { scope: string[]; user: string[] },
    matchMode: FilterMatchMode = 'all'
  ): string {
    const clauses = [...scope];
    if (user.length) {
      if (matchMode === 'any' && user.length > 1) {
        clauses.push(`(${user.join(' || ')})`);
      } else {
        clauses.push(...user);
      }
    }
    return clauses.join(' && ');
  }

  private mapHit(
    hit: TypesenseSearchResponseHit<SearchResultDocument>
  ): SearchHit {
    return {
      document: hit.document,
      highlights: (hit.highlights || []).map((h) => ({
        field: h.field as string,
        snippet: h.snippet || '',
      })),
    };
  }

  private mapResponse(
    response: TypesenseSearchResponse<SearchResultDocument>
  ): SearchResponse {
    return {
      found: response.found,
      groupedHits: (response.grouped_hits || []).map((group) => ({
        groupKey: group.group_key[0],
        found: group.found || group.hits.length,
        hits: group.hits.map((h) => this.mapHit(h)),
      })),
    };
  }

  async search({
    query,
    limit,
    informationTypes,
    informationSubjects,
    informationUnits,
    documentTypes,
    components,
    filterMatchMode,
    cid,
    language,
  }: SearchParams): Promise<SearchResponse | null> {
    if (!query) {
      console.error('No current search query');
      return null;
    }
    if (!this.informationUnitCollection) {
      console.warn(`No information unit collection configured`);
      return null;
    }
    if (!(await this.isHealthy())) {
      console.warn(`Typesense server is not healthy`);
      return null;
    }

    const filterBy = this.combineFilterBy(
      this.buildFilterBy({
        informationTypes,
        informationSubjects,
        informationUnits,
        documentTypes,
        components,
        cid,
        language,
      }),
      filterMatchMode
    );
    const multiSearchResponse = await this.client.multiSearch.perform({
      searches: [
        {
          collection: this.informationUnitCollection,
          q: query,
          query_by: 'title,content',
          filter_by: filterBy,
          // Note: It's safer to pass group_by as a comma-separated string in multiSearch
          group_by: ['iri', 'productId'].join(','),
          limit,
        },
      ],
    });

    // Grab the first result from the array so it acts exactly like your old 'response'
    const response = multiSearchResponse
      .results[0] as TypesenseSearchResponse<SearchResultDocument>;
    return this.mapResponse(response);
  }

  async more(
    {
      query,
      limit,
      informationTypes,
      informationSubjects,
      documentTypes,
      components,
      informationUnits,
      filterMatchMode,
      cid,
      language,
    }: SearchParams,
    iri: string,
    pageIndex: number,
    pageSize: number
  ): Promise<SearchHit[] | null> {
    if (!query) {
      console.error('No current search query');
      return null;
    }
    if (!this.informationUnitCollection) {
      console.warn(`No information unit collection configured`);
      return null;
    }
    if (!(await this.isHealthy())) {
      console.warn(`Typesense server is not healthy`);
      return null;
    }

    const clauses = this.buildFilterBy({
      informationTypes,
      informationSubjects,
      informationUnits,
      documentTypes,
      components,
      cid,
      language,
    });
    // Pin the expanded group's IRI as a scope clause so it is always AND'ed,
    // even when the user filters are OR'ed together in "any" mode.
    clauses.scope.push(`iri:=\`${iri}\``);
    const filterBy = this.combineFilterBy(clauses, filterMatchMode);

    const response = await this.client
      .collections<SearchResultDocument>(this.informationUnitCollection)
      .documents()
      .search({
        q: query,
        query_by:
          'title,content,informationTypes_labels,productMetadata_labels',
        filter_by: filterBy,
        per_page: pageSize,
        page: pageIndex,
        limit,
      });

    return (response.hits || []).map((h) => this.mapHit(h));
  }
}
